import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  FormControl,
  Box,
  Pagination,
  TextField,
  useMediaQuery,
  Select,
  MenuItem,
  Typography,
  Container,
  Grid,
  Button,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { toUpper } from 'lodash';
import { actions } from '../../../actions';
import Text from '../../../shared/Text';
import { institutionalSM } from '../../../constants/Inputs';
import Message from '../components/Message';
import { typeByName } from '../../../constants/Filters';
import {
  handleMessageAction,
  formatMessageToDB,
  validateInputs,
  checkDuplicateMessageId,
} from '../helpers';
import { handleErrorSwal } from '../../../shared/swals';

const useStyles = (small) =>
  makeStyles((theme) => ({
    appBarSpacer: theme.mixins.toolbar,
    institutionalMessages: {
      paddingBottom: 32,
    },
    filtersContainer: {
      marginTop: theme.spacing(1),
    },
    addButtonContainer: {
      height: '100%',
    },
    form: {
      marginTop: theme.spacing(1),
    },
    formControl: {
      marginBlock: theme.spacing(1),
      minWidth: 120,
    },
    submitButtonContainer: {
      marginTop: theme.spacing(3),
    },
    sectionTitle: {
      color: '#747474',
    },
    addLy: {
      margin: !small && '16px',
      marginTop: small && '16px',
    },
    selectRef: {
      fontSize: theme.sizes.sm.t1,
      color: '#bbb',
    },
  }));

export default function InstitutionalMessages() {
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();
  const firebase = useFirebase();
  const profile = useSelector((state) => state.firebase.profile);
  const organization = useSelector((state) => state.app.organization);
  const configuration = useSelector((state) => state.app.labelsConfiguration);
  const messages = useSelector((state) => state.app.messages);
  const appDispatch = useDispatch();
  const config = useSelector((state) => state.app.prestobotsConfig);

  const [showAddInstitutionalMessage, setShowAddInstitutionalMessage] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [originType, setOriginType] = useState(-1);

  const myRef = useRef(null);

  const isAdmin = profile && profile.claims && profile.claims.includes('admin');

  useEffect(() => {
    if (page !== 1) setPage(1);
  }, [search, originType]);

  const generateToken = async () => {
    try {
      const token = await firebase.auth().currentUser.getIdToken();
      return token;
    } catch (err) {
      console.log(err);
    }
  };
  const institutionalMessages =
    (messages && messages.filter((e) => e.type === 'institutional')) || [];

  const newInstitutionalMessage = async (institutionalMessage) => {
    const token = await generateToken();
    const institutionalMessageDB = formatMessageToDB({
      message: institutionalMessage,
      type: 'institutional',
      organizationId: organization.id,
    });
    institutionalMessageDB.channel = originType;

    const isDuplicateMessage = checkDuplicateMessageId(
      institutionalMessages,
      institutionalMessageDB,
    );
    if (isDuplicateMessage) {
      appDispatch({
        type: actions.alert,
        alert: {
          snackbarOpen: true,
          snackbarMessage: `Ya existe un mensaje igual`,
          snackbarSeverity: 'error',
        },
      });
      return;
    }
    await handleMessageAction({
      message: institutionalMessageDB,
      action: 'Crear',
      icon: 'success',
      actionApi: 'add',
      appDispatch,
      messages,
      token,
      originType,
    });
    setShowAddInstitutionalMessage(false);
  };

  const updateInstitutionalMessage = async (message) => {
    let isDuplicateMessage;
    const foundMessage = institutionalMessages?.find(
      (institutionalMessage) => institutionalMessage.id === message.id,
    );
    if (foundMessage) {
      if (foundMessage.messageId !== message.messageId)
        isDuplicateMessage = checkDuplicateMessageId(institutionalMessages, message);

      if (isDuplicateMessage) {
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: 'Ya existe un mensaje con el mismo ID y canal',
            snackbarSeverity: 'error',
          },
        });
        return;
      }
      message.messageId = message.messageId.trim().toUpperCase();
      const token = await generateToken();
      await handleMessageAction({
        message,
        action: 'Actualizar',
        icon: 'info',
        actionApi: 'edit',
        appDispatch,
        messages,
        token,
        originType,
      });
    } else {
      await handleErrorSwal();
    }
  };

  const handleDelete = async (message) => {
    const token = await generateToken();
    await handleMessageAction({
      message,
      action: 'Borrar',
      icon: 'error',
      actionApi: 'delete',
      appDispatch,
      messages,
      token,
      originType,
    });
  };

  const handleSubmit = ({ id, index, ...message }, mode, callback) => {
    if (!validateInputs(message)) {
      appDispatch({
        type: actions.alert,
        alert: {
          snackbarOpen: true,
          snackbarMessage: `DEBE COMPLETAR TODOS LOS CAMPOS`,
          snackbarSeverity: 'error',
        },
      });
      return;
    }

    switch (mode) {
      case 'create':
        newInstitutionalMessage(message);
        break;
      case 'update':
        updateInstitutionalMessage({ ...message, id: id });
        break;
    }
  };

  const institutionalSMParams = {
    values:
      (config && [
        ...config.states
          ?.filter(
            (state) =>
              organization && organization.states && organization.states.includes(state.key),
          )
          .map((state) => ({ value: parseInt(state.key), label: state.label })),
        { value: 3, label: 'terminado' },
      ]) ||
      [],
    isAdmin,
    showFile: organization.businessRules?.institucionalMessageMedia,
  };
  const inputsCreate = institutionalSM({ onCreate: true, ...institutionalSMParams });

  const inputsUpdate = institutionalSM({ onCreate: false, ...institutionalSMParams });

  const filteredMessages = institutionalMessages
    .map((e, index) => ({ ...e, index }))
    .filter((institutionalMessage) => {
      return !search
        ? true
        : toUpper(institutionalMessage.value).indexOf(toUpper(search)) !== -1 ||
            toUpper(institutionalMessage.messageId).indexOf(toUpper(search)) !== -1;
    })
    .filter((e) => {
      return originType === -1 || (originType !== -1 && e.channel === originType);
    })
    .sort((a, b) => {
      const dateA = a.creationDate || Number.NEGATIVE_INFINITY;
      const dateB = b.creationDate || Number.NEGATIVE_INFINITY;
      return dateB - dateA;
    });

  const pages =
    filteredMessages.length % 10 === 0
      ? parseInt(filteredMessages.length / 10)
      : parseInt(filteredMessages.length / 10) + 1;

  return (
    <div className="App">
      <Container className={classes.institutionalMessages} maxWidth="lg">
        {profile.isLoaded && (
          <Box ref={myRef}>
            <Grid container direction="row" className={classes.filtersContainer}>
              <Grid item xs={12}>
                {configuration && configuration.institutionalMessages && (
                  <Text configuration={configuration} propName="institutionalMessages" />
                )}
              </Grid>
              <Grid item xs={12} sm={3}>
                <Grid container>
                  <Grid item xs={12} sm={11}>
                    <FormControl fullWidth variant="outlined" className={classes.formControl}>
                      <Typography className={classes.selectRef}>Origen</Typography>
                      <Select
                        value={originType}
                        onChange={(event) => setOriginType(event.target.value)}
                      >
                        <MenuItem value={-1} disabled>
                          Selecciona una opción
                        </MenuItem>
                        <MenuItem value={typeByName.web}>WEB</MenuItem>
                        <MenuItem value={typeByName.whatsapp}>WHATSAPP</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Box display="flex" alignItems="flex-end" minWidth="300px">
                <Grid container>
                  <Grid item xs={12} sm={11}>
                    <FormControl fullWidth className={classes.formControl}>
                      <TextField
                        id="standard-basic"
                        variant="outlined"
                        label={
                          originType !== -1 ? 'Buscar por valor' : 'Seleccione opcion para buscar'
                        }
                        disabled={originType === -1}
                        onChange={(event) => setSearch(event.target.value)}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                {originType !== -1 && !showAddInstitutionalMessage && (
                  <Box className={classes.addLy}>
                    {isAdmin && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setShowAddInstitutionalMessage(!showAddInstitutionalMessage)}
                      >
                        Agregar
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
            {originType !== -1 && (
              <Box>
                {pages > 1 && filteredMessages.length > 0 ? (
                  <Box display="flex" justifyContent="center" my={4}>
                    <Pagination
                      siblingCount={0}
                      count={pages}
                      color="primary"
                      page={page}
                      onChange={(e, value) => setPage(value)}
                    />
                  </Box>
                ) : null}
                {showAddInstitutionalMessage && (
                  <Message
                    key={-1}
                    inputs={inputsCreate}
                    initialState={{ reply: true, showFreeApps: false }}
                    mode="create"
                    index={-1}
                    type=""
                    onDanger={() => setShowAddInstitutionalMessage(false)}
                    onSuccess={handleSubmit}
                  />
                )}
                {filteredMessages
                  .slice((page - 1) * 10, page * 10)
                  .map((institutionalMessage, index) => (
                    <Message
                      key={index}
                      inputs={inputsUpdate}
                      initialState={{
                        ...institutionalMessage,
                      }}
                      mode="update"
                      index={index}
                      type=""
                      onDanger={handleDelete}
                      onSuccess={handleSubmit}
                    />
                  ))}
                {pages > 1 && filteredMessages.length > 0 ? (
                  <Box display="flex" justifyContent="center" my={4}>
                    <Pagination
                      siblingCount={0}
                      count={pages}
                      color="primary"
                      page={page}
                      onChange={(e, value) => setPage(value)}
                    />
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
        )}
      </Container>
    </div>
  );
}
